import { endpoints } from "@/configs/Settings";
import { useContractContext } from "@/contexts/ContractContext";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { PostAppointment, PostSlot } from "@/services/fhir";
import {
  buildClinicalAutomationTriggerInputFromAppointmentForm,
  buildScheduleManagerRequesterReference,
  triggerPreConsultationAutomation,
} from "@/services/crm/triggerClinicalAutomation";
import { PostPaymentNotice } from "@/services/fhir/paymentnotice/PostPaymentNotice";
import { PostWhereby } from "@/services/whereby/PostWhereby";
import { ISlotData } from "@/utils/interfaces";
import { useFormatter } from "@/utils/useFormatter";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  EVOLUTION_NOTE,
  EVOLUTION_TYPE_SERVICE,
} from "@/components/schedule/evolution/constants";

interface UseAppointmentSubmissionProps {
  setValue: any;
  setModalOpen: any;
  setTabRefetch?: (value: boolean | ((prev: boolean) => boolean)) => void;
  walkin: boolean;
  control: any;
  onSuccess?: () => void;
}

export function useAppointmentSubmission({
  setValue,
  setModalOpen,
  setTabRefetch,
  walkin,
  control,
  onSuccess,
}: UseAppointmentSubmissionProps) {
  const { extractNumericValue } = useFormatter;
  const { location: locationContext } = useLocationContext();
  const { organization } = useOrganizationContext();
  const { user } = useSessionContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const { currentContract } = useContractContext();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);

  // Função centralizada para criar slot (usado em walkin)
  const createSlot = async (data: any, timeStart: string, timeEnd: string) => {
    const slotData: ISlotData = {
      type: data?.typeService,
      scheduleId: data?.schedule,
      start: dayjs(timeStart)?.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
      end: dayjs(timeEnd)?.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    };
    return await PostSlot(slotData);
  };

  // Função centralizada para configurar serviço virtual
  const setupVirtualService = async (data: any, endTime: string) => {
    try {
      const auxData = {
        endDate: dayjs(endTime)?.format("YYYY-MM-DDTHH:mm:ss-03:00"),
        roomNamePrefix: `${endpoints?.AppName}-whereby`,
        roomNamePattern: "human-short",
      };
      const response = await PostWhereby(auxData);

      return {
        ...data,
        virtualService: [
          {
            addressUrl: response.roomUrl,
            sessionKey: response.meetingId || "",
          },
        ],
      };
    } catch (err) {
      // Erro silencioso - retorna dados sem serviço virtual
      return data;
    }
  };

  // Função centralizada para criar aviso de pagamento
  const createPaymentNotice = async (
    appointmentId: string,
    slotId: string,
    data: any,
    appointmentIsFree: boolean
  ) => {
    if (!data?.serviceCost || data?.serviceCost === "" || appointmentIsFree) {
      return;
    }

    const watchLocation = data?.locationActor;

    const dataPaymentNotice: any = {
      resourceType: "PaymentNotice",
      status: "active",
      extension: [
        {
          url: "http://fluxmed.com.br/fhir/StructureDefinition/AppointmentReference",
          valueReference: {
            reference: `Appointment/${appointmentId}`,
          },
        },
        {
          url: "http://fluxmed.com.br/fhir/StructureDefinition/OriginalServiceCost",
          valueDecimal: data?.originalServiceCost
            ? extractNumericValue(data?.originalServiceCost)
            : 0,
        },
      ],
      identifier: [
        {
          system: "https://fluxmed.com.br/fhir/identifier/organization",
          value: `Organization/${organization?.id}`,
        },
        {
          system: "https://fluxmed.com.br/fhir/identifier/location",
          value: `${watchLocation?.reference}`,
        },
      ],
      response: {
        reference: `${data?.patientActor?.reference}`,
      },
      request: {
        reference: `Slot/${slotId}`,
      },
      created: dayjs().format(),
      reporter: { reference: `${data?.professionalActor?.reference}` },
      recipient: { reference: `Organization/${organization?.id}` },
      amount: {
        value: `${extractNumericValue(data?.serviceCost)}`,
        currency: "BRL",
      },
    };

    await PostPaymentNotice(dataPaymentNotice);
  };

  // Função principal de submissão centralizada
  const submitAppointment = async (data: any) => {
    setValue("appointmentIsFree", data.appointmentIsFree);

    try {
      // Prepara dados iniciais
      if (!hasFluxPayExtension || !data.serviceCost) {
        data = {
          ...data,
          serviceCost: "",
          appointmentIsFree: true,
        };
      }

      setLoading(true);

      // Evolução: sempre gratuita e com note de atendimento sem presença.
      if (data.typeService === EVOLUTION_TYPE_SERVICE) {
        data = {
          ...data,
          serviceCost: "",
          appointmentIsFree: true,
          note: data.note || EVOLUTION_NOTE,
        };
      }

      let createdAppointment: any = null;

      // Encaixe e evolução sem vaga livre: cria o Slot antes do Appointment.
      const shouldCreateSlot =
        walkin ||
        data.typeService === "WALKIN" ||
        (data.typeService === EVOLUTION_TYPE_SERVICE && !data.slotInfo?.id);

      if (shouldCreateSlot) {
        const createdSlot = await createSlot(
          data,
          data.timeStart,
          data.timeEnd
        );

        // Configura serviço virtual se necessário
        if (data?.isVirtual) {
          data = await setupVirtualService(data, createdSlot?.end);
        }

        // Cria o agendamento
        createdAppointment = await PostAppointment({
          ...data,
          slotInfo: {
            id: createdSlot?.id,
            start: createdSlot?.start,
            end: createdSlot?.end,
          },
        });

        // Cria aviso de pagamento se necessário
        await createPaymentNotice(
          createdAppointment?.id,
          createdSlot?.id,
          data,
          data.appointmentIsFree
        );
      } else {
        // Lógica para agendamento normal
        if (data?.isVirtual) {
          data = await setupVirtualService(data, data.slotInfo?.end);
          if (!data.appointmentIsFree) {
            data.serviceCost = data.serviceCost;
          }
        }

        // Cria o agendamento
        createdAppointment = await PostAppointment(data);

        // Cria aviso de pagamento se necessário
        await createPaymentNotice(
          createdAppointment?.id,
          data?.slotInfo?.id,
          data,
          data.appointmentIsFree
        );
      }

      // Pré-consulta: dispara automação somente quando appointment já nasce booked.
      // Evolução não dispara (paridade com usePatientEvolution; sem presença do paciente).
      if (
        createdAppointment?.status === "booked" &&
        data.typeService !== EVOLUTION_TYPE_SERVICE
      ) {
        try {
          const triggerInput =
            buildClinicalAutomationTriggerInputFromAppointmentForm({
              organizationId: organization?.id,
              professionalActorReference: data?.professionalActor?.reference,
              patientActorReference: data?.patientActor?.reference,
              patientDisplayName: data?.patientActor?.display,
              patientCpf: data?.cpf,
              appointmentId: createdAppointment.id,
              appointmentStartAtIso:
                createdAppointment.start ?? data?.slotInfo?.start,
              requesterReference: buildScheduleManagerRequesterReference({
                organizationId: organization?.id,
                practitionerUserId: user?.username,
              }),
            });
          if (triggerInput) {
            await triggerPreConsultationAutomation(triggerInput);
          }
        } catch (automationError) {
          console.error("[automationTrigger] pré-consulta:", automationError);
        }
      }

      // Sucesso
      toast.success(
        "Vaga preenchida com sucesso, visualize em próximas consultas."
      );

      // Chama callback de sucesso se fornecido.
      // Se não houver callback e também não houver função de refetch,
      // faz navegação padrão para a home.
      if (onSuccess) {
        onSuccess();
      } else if (!setTabRefetch) {
        navigate("/");
      }
    } catch (err) {
      // Erro silencioso – não disparamos refetch extra aqui
      // para evitar múltiplos toggles que anulariam a mudança.
    } finally {
      // Garante que o refetch será disparado exatamente uma vez
      // após o término do fluxo (sucesso ou erro).
      if (setTabRefetch) {
        setTabRefetch((prev: boolean) => !prev);
      }
      setLoading(false);
      setModalOpen(false);
    }
  };

  return {
    submitAppointment,
    loading,
  };
}
